import { useCallback, useEffect } from 'react';
import { dispatch, useSelector } from '../redux/store';
import { checkPmsEnabled } from '../redux/slices/pmsSchedule';
import { fetchMyPermissions } from '../redux/slices/permissions';

// Cold routes and booking entry points share the same practice-stamped request.
// A false initial boolean is not an authoritative non-native answer.
export default function usePmsSchedulingScope(practiceId) {
    const scheduling = useSelector((state) => state.pmsSchedule);
    const permissions = useSelector((state) => state.permissions || {});
    const target = practiceId == null ? null : String(practiceId);
    const current = target && scheduling.enablementPracticeId === target;
    const permissionFetchFailed = permissions.lastFetchStatus != null && permissions.lastFetchStatus !== 200;
    const permissionCurrent = !permissionFetchFailed && target && String(permissions.practiceId) === target
        && !permissions.loading && String(permissions.loadingPracticeId ?? permissions.practiceId) === target;
    const permissionError = target && !permissions.loading
        && String(permissions.loadingPracticeId) === target
        && (permissionFetchFailed || String(permissions.practiceId) !== target);
    const resolved = Boolean(current && scheduling.enablementStatus === 'ready' && permissionCurrent);
    const level = permissionCurrent ? permissions.permissions?.['pms-scheduling'] : null;
    const denied = resolved && scheduling.pmsEnabledGlobal && !['view', 'manage'].includes(level);
    const ready = resolved && !denied;
    const error = Boolean((current && scheduling.enablementStatus === 'error') || permissionError || denied);
    const enabled = ready && scheduling.pmsEnabledGlobal;
    useEffect(() => {
        if (target) dispatch(checkPmsEnabled(target));
    }, [target]);
    const retry = useCallback(() => {
        if (!target) return;
        dispatch(checkPmsEnabled(target, { force: true }));
        if (!permissionCurrent || denied) dispatch(fetchMyPermissions(target));
    }, [target, permissionCurrent, denied]);
    return { denied, ready, pending: !ready && !error, error, enabled: Boolean(enabled), retry };
}
